import {
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  IndianRupee,
  Package,
  RotateCcw,
  ShoppingBag,
  Truck,
  User,
  X
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminOrders } from "../../hooks/useAdminOrder";
import { AdminOrderService } from "../../services/adminOrderService";
import useToastStore from "../../store/useToastStore";
import { Order, OrderStatus } from "../../types/order";
import { formatDateTime } from "../../utils/date";
import { formatPrice } from "../../utils/utility";
import ShippingDetailsModal from "../admin/ShippingDetailsModal";
import SearchInput from "./SearchInput";
import { FaWhatsapp } from 'react-icons/fa';

interface Filters {
  search: string;
  dateFrom: string;
  dateTo: string;
}

interface OrderListProps {
  status: string;
}

const OrderList = ({ status }: OrderListProps) => {
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  // State for modals
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<() => void>(() => { });
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [isShipping, setIsShipping] = useState(false);

  // State for filters and pagination with localStorage persistence
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(`orderList_${status}_currentPage`);
      const parsed = saved ? parseInt(saved, 10) : 1;
      return isNaN(parsed) || parsed < 1 ? 1 : parsed;
    } catch (error) {
      return 1;
    }
  });

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(`orderList_${status}_itemsPerPage`);
      const parsed = saved ? parseInt(saved, 10) : 10;
      const validValues = [5, 10, 25, 50];
      return validValues.includes(parsed) ? parsed : 10;
    } catch (error) {
      return 10;
    }
  });

  const [filters, setFilters] = useState<Filters>(() => {
    try {
      const saved = localStorage.getItem(`orderList_${status}_filters`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load filters from localStorage:', error);
    }
    return {
      search: "",
      dateFrom: "",
      dateTo: ""
    };
  });

  // Debounced search state for UI feedback
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(filters.search);
  const [isSearchDebouncing, setIsSearchDebouncing] = useState(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(`orderList_${status}_filters`, JSON.stringify(filters));
    } catch (error) {
      console.warn('Failed to save filters to localStorage:', error);
    }
  }, [filters, status]);

  // Save pagination state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`orderList_${status}_currentPage`, currentPage.toString());
    } catch (error) {
      console.warn('Failed to save currentPage to localStorage:', error);
    }
  }, [currentPage, status]);

  useEffect(() => {
    try {
      localStorage.setItem(`orderList_${status}_itemsPerPage`, itemsPerPage.toString());
    } catch (error) {
      console.warn('Failed to save itemsPerPage to localStorage:', error);
    }
  }, [itemsPerPage, status]);

  // Reset to page 1 when filters change (except for search which has debouncing)
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) {
      setCurrentPage(1);
    }
  }, [filters.dateFrom, filters.dateTo]);

  // Debounced search effect
  useEffect(() => {
    setIsSearchDebouncing(true);

    const timer = setTimeout(() => {
      setDebouncedSearchValue(filters.search);
      setIsSearchDebouncing(false);

      // Reset to page 1 when search changes
      if (filters.search && currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search, currentPage]);

  // Prepare query parameters with useMemo to prevent unnecessary re-renders
  const queryParams = useMemo(() => {
    // Add 1 day to dateTo to include the entire selected day
    let adjustedDateTo = undefined;
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setDate(toDate.getDate() + 1);
      adjustedDateTo = toDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    }

    return {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchValue || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: adjustedDateTo,
    };
  }, [currentPage, itemsPerPage, debouncedSearchValue, filters.dateFrom, filters.dateTo]);

  // Use the hook with parameters
  const { data: response, isLoading, error, refetch, isFetching } = useAdminOrders(status, queryParams);

  // Extract data from response
  const orders = response?.orders || [];
  const totalCount = response?.totalCount || 0;
  const totalPages = response?.totalPages || 1;
  const hasNextPage = response?.hasNextPage || false;
  const hasPrevPage = response?.hasPrevPage || false;

  const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus, shippingDetails?: any) => {
    try {
      // Set loading state for shipping operations
      if (newStatus === 'SHIPPED') {
        setIsShipping(true);
      }

      // Use shipOrder for SHIPPED status with shipping details
      if (newStatus === 'SHIPPED' && shippingDetails) {
        await AdminOrderService.shipOrder(orderId, shippingDetails);
        const method = shippingDetails.shippingMethod === 'partner' ? 'with partner' : 'manually';
        showToast(`Order shipped ${method} successfully`, 'success');
      } else {
        // Use existing method for other status updates
        await AdminOrderService.updateOrderStatus(orderId, newStatus, shippingDetails);
        showToast('Order status updated successfully', 'success');
      }

      // Refresh the orders list after successful update
      await refetch();

    } catch (error: any) {
      console.log(error);
      const method = shippingDetails?.shippingMethod === 'partner' ? 'partner shipping' : 'order status update';
      const errorMessage = error.response?.data?.message || error.message;
      showToast(errorMessage, 'error');
      console.error('Error updating order status:', error);
    } finally {
      // Clear loading state
      setIsShipping(false);
    }
  };

  const handleVerifyAdvancePayment = async (orderId: string) => {
    try {
      await AdminOrderService.verifyAdvancePayment(orderId);
      showToast('Advance payment verified successfully', 'success');
      await refetch();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      showToast(errorMessage, 'error');
    }
  };

  const handleConfirmAction = (action: () => void, message: string) => {
    setConfirmationAction(() => action);
    setConfirmationMessage(message);
    setShowConfirmationModal(true);
  };

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: "",
      dateFrom: "",
      dateTo: ""
    });
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Create stable filter update handlers
  const handleSearchChange = useCallback((value: string) => {
    setFilters((prev: Filters) => ({ ...prev, search: value }));
  }, []);

  const handleDateFromChange = useCallback((value: string) => {
    setFilters((prev: Filters) => ({ ...prev, dateFrom: value }));
  }, []);

  const handleDateToChange = useCallback((value: string) => {
    setFilters((prev: Filters) => ({ ...prev, dateTo: value }));
  }, []);

  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      NEW: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'New' },
      PENDING: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Pending' },
      CONFIRMED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Confirmed' },
      SHIPPED: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Shipped' },
      DELIVERED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Delivered' },
      CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Cancelled' }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'NEW': return <ShoppingBag className="w-4 h-4" />;
      case 'PENDING': return <CheckCircle className="w-4 h-4" />;
      case 'CONFIRMED': return <Check className="w-4 h-4" />;
      case 'SHIPPED': return <Truck className="w-4 h-4" />;
      case 'DELIVERED': return <CheckCircle className="w-4 h-4" />;
      case 'CANCELLED': return <X className="w-4 h-4" />;
      default: return <ShoppingBag className="w-4 h-4" />;
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrevPage}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}</span>
              {' '}to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span>
              {' '}of{' '}
              <span className="font-medium">{totalCount}</span>
              {' '}results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="space-y-4">
        {/* Search and Filter Bar - Always visible to prevent focus loss */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col space-y-4">
            {/* Search and Date Filters in One Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SearchInput
                key="search-input"
                value={filters.search}
                onChange={handleSearchChange}
                placeholder="Search by Order ID and name..."
                label="Search"
                isLoading={isSearchDebouncing}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleClearFilters}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear Filters
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={isLoading}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">entries</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <X className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-red-500 font-medium">Error loading orders</p>
            <p className="text-gray-500 text-sm">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar - Always visible to prevent focus loss */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col space-y-4">
          {/* Search and Date Filters in One Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SearchInput
              key="search-input"
              value={filters.search}
              onChange={handleSearchChange}
              placeholder="Search by Order ID and name..."
              label="Search"
              isLoading={isSearchDebouncing}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateToChange(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleClearFilters}
                disabled={isLoading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear Filters
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={isLoading}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>
          </div>
        </div>
      </div>


      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="text-gray-500">Loading orders...</p>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-8 h-8 text-indigo-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filters.search || filters.dateFrom || filters.dateTo ?
                'No orders found' :
                `No ${status.charAt(0) + status.slice(1).toLowerCase()} Orders`
              }
            </h3>
            <p className="text-gray-500 text-center max-w-sm">
              {filters.search || filters.dateFrom || filters.dateTo ?
                'Try adjusting your search criteria or filters.' :
                `There are currently no ${status.toLowerCase()} orders in your store.`
              }
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order: Order) => (
                    <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap align-top">
                        <div className="text-sm font-medium text-gray-900">#{order.orderId}</div>
                        <div className="text-sm text-gray-500">{formatDateTime(order.createdAt)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{order.shippingAddress.name}</div>
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-500">{order.shippingAddress.mobile}</div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const cleaned = order.shippingAddress.mobile.replace(/\D/g, '');
                                  const whatsappNumber = cleaned.length === 10 ? `91${cleaned}` : cleaned;
                                  window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                                }}
                                className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                                title="Chat on WhatsApp"
                              >
                                <FaWhatsapp className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 w-40 max-w-60 align-top">
                        <div className="w-full space-y-1">
                          {order.items?.map((item, index) => {
                            const variants = Object.entries(item.attributes ?? {});
                            return (
                              <div key={index} className="text-xs text-gray-900">
                                <span className="truncate block" title={item.title}>{item.quantity} × {item.title}</span>
                                {variants.length > 0 && (
                                  <span className="text-gray-400">
                                    {variants.map(([k, v]) => `${k}: ${v}`).join(", ")}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right align-top">
                        <div className="text-sm font-semibold text-gray-900">{formatPrice(order.amount)}</div>
                        {order.paymentOption === 'PARTIAL_COD' && (
                          <div className="inline-flex items-center mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              order.paymentStatus === 'PAID' || order.paymentStatus === 'PARTIALLY_PAID'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}>
                              ₹{order.partialPaymentAmount} Advance
                            </span>
                          </div>
                        )}
                        {order.paymentStatus === 'PARTIALLY_PAID' && order.paymentOption !== 'PARTIAL_COD' && (
                          <div className="inline-flex items-center mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              Partially Paid
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-left">
                        <div className="text-sm font-semibold text-gray-900">
                          {order.paymentOption}
                        </div>
                        {order.paymentOption === 'PARTIAL_COD' && order.advancePaymentOption && (
                          <div className="text-xs text-gray-500 font-medium mt-1">
                            via {order.advancePaymentOption}
                          </div>
                        )}
                        {/* Manual UPI Payment Indicator */}
                        {order.payment?.paymentMethod === 'MANUAL_UPI' && order.status === 'NEW' && (
                          <div className="inline-flex items-center mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                              <svg className="w-3 h-3 mr-1 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              Receipt Available
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center align-top">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right align-top">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/orders/${order._id}`)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </button>

                          {order.status === 'NEW' && (
                            <button
                              onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'PENDING'), 'Are you sure you want to verify payment for this order?')}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verify
                            </button>
                          )}

                          {/* PARTIAL_COD + MANUAL_UPI: Verify Advance first, then Confirm */}
                          {order.status === 'PENDING' &&
                           order.paymentOption === 'PARTIAL_COD' &&
                           order.advancePaymentOption === 'MANUAL_UPI' &&
                           order.paymentStatus === 'PENDING' && (
                            <button
                              onClick={() => handleConfirmAction(() => handleVerifyAdvancePayment(order._id), 'Have you received the advance UPI payment for this order?')}
                              className="inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verify Advance
                            </button>
                          )}

                          {/* PARTIAL_COD + MANUAL_UPI: Show Confirm after advance verified */}
                          {order.status === 'PENDING' &&
                           order.paymentOption === 'PARTIAL_COD' &&
                           order.advancePaymentOption === 'MANUAL_UPI' &&
                           (order.paymentStatus === 'PARTIALLY_PAID' || order.paymentStatus === 'PAID') && (
                            <button
                              onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Confirm
                            </button>
                          )}

                          {/* PARTIAL_COD + PHONEPE/RAZORPAY: Auto-handled, show waiting chip */}
                          {order.status === 'PENDING' &&
                           order.paymentOption === 'PARTIAL_COD' &&
                           (order.advancePaymentOption === 'PHONEPE' || order.advancePaymentOption === 'RAZORPAY' || order.advancePaymentOption === 'CASHFREE') &&
                           order.paymentStatus === 'PENDING' && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Awaiting Payment
                            </span>
                          )}

                          {/* PARTIAL_COD + PHONEPE/RAZORPAY: Advance paid, show Confirm */}
                          {order.status === 'PENDING' &&
                           order.paymentOption === 'PARTIAL_COD' &&
                           (order.advancePaymentOption === 'PHONEPE' || order.advancePaymentOption === 'RAZORPAY' || order.advancePaymentOption === 'CASHFREE') &&
                           (order.paymentStatus === 'PARTIALLY_PAID' || order.paymentStatus === 'PAID') && (
                            <button
                              onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Confirm
                            </button>
                          )}

                          {/* Non-Partial-COD: Standard PENDING → CONFIRM */}
                          {order.status === 'PENDING' &&
                           order.paymentOption !== 'PHONEPE' &&
                           order.paymentOption !== 'RAZORPAY' &&
                           order.paymentOption !== 'CASHFREE' &&
                           order.paymentOption !== 'PARTIAL_COD' && (
                            <button
                              onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Confirm
                            </button>
                          )}

                          {order.status === 'CONFIRMED' && (
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowShippingModal(true);
                              }}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                            >
                              <Package className="w-3 h-3 mr-1" />
                              Ship
                            </button>
                          )}

                          {order.status === 'SHIPPED' && (
                            <button
                              onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'DELIVERED'), 'Are you sure you want to mark this order as delivered?')}
                              className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Deliver
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderPagination()}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {orders.map((order: Order) => (
              <div key={order._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">#{order.orderId}</h3>
                    <p className="text-xs text-gray-500">{formatDateTime(order.createdAt)}</p>
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900 font-medium">{order.shippingAddress.name}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const cleaned = order.shippingAddress.mobile.replace(/\D/g, '');
                        const whatsappNumber = cleaned.length === 10 ? `91${cleaned}` : cleaned;
                        window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                      }}
                      className="flex items-center gap-1.5 px-2 py-1 bg-green-50 text-[#25D366] rounded-md text-xs font-bold active:scale-95 transition-all"
                    >
                      <FaWhatsapp className="w-3.5 h-3.5" />
                      Chat
                    </button>
                  </div>
                  <div className="text-sm">
                    <div className="flex items-center mb-1">
                      <ShoppingBag className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600 font-medium">Items:</span>
                    </div>
                    <div className="ml-6 space-y-1 max-w-xs">
                      {order.items?.map((item, index) => {
                        const variants = Object.entries(item.attributes ?? {});
                        return (
                          <div key={index} className="text-xs">
                            <span className="text-gray-900 truncate block" title={item.title}>{item.quantity} × {item.title}</span>
                            {variants.length > 0 && (
                              <span className="text-gray-400">
                                {variants.map(([k, v]) => `${k}: ${v}`).join(", ")}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center text-sm">
                    <IndianRupee className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-900 font-medium">{formatPrice(order.amount)}</span>
                    {/* Partial COD indicator for Mobile */}
                    {order.paymentOption === 'PARTIAL_COD' && (
                      <span className={`inline-flex items-center ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                        order.paymentStatus === 'PAID' || order.paymentStatus === 'PARTIALLY_PAID'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        ₹{order.partialPaymentAmount} Advance
                      </span>
                    )}
                    {/* PARTIALLY_PAID badge for non-PARTIAL_COD orders */}
                    {order.paymentStatus === 'PARTIALLY_PAID' && order.paymentOption !== 'PARTIAL_COD' && (
                      <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        Partially Paid
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col text-sm space-y-1">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="text-gray-400 mr-1">Payment:</span>
                      <span className="text-gray-800 font-semibold">{order.paymentOption}</span>
                      {order.advancePaymentOption && order.paymentOption === 'PARTIAL_COD' && (
                        <span className="text-xs text-gray-500 font-medium">
                          (via {order.advancePaymentOption})
                        </span>
                      )}
                    </div>
                    {/* Manual UPI Payment Indicator for Mobile */}
                    {order.payment?.paymentMethod === 'MANUAL_UPI' && order.status === 'NEW' && (
                      <span className="inline-flex items-center w-fit px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                        <svg className="w-3 h-3 mr-1 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Receipt Available
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigate(`/orders/${order._id}`)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Details
                    </button>

                    {order.status === 'NEW' && (
                      <button
                        onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'PENDING'), 'Are you sure you want to verify payment for this order?')}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verify
                      </button>
                    )}

                    {/* Mobile: PARTIAL_COD + MANUAL_UPI — Verify Advance */}
                    {order.status === 'PENDING' &&
                     order.paymentOption === 'PARTIAL_COD' &&
                     order.advancePaymentOption === 'MANUAL_UPI' &&
                     order.paymentStatus === 'PENDING' && (
                      <button
                        onClick={() => handleConfirmAction(() => handleVerifyAdvancePayment(order._id), 'Have you received the advance UPI payment for this order?')}
                        className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verify Advance
                      </button>
                    )}

                    {/* Mobile: PARTIAL_COD + MANUAL_UPI — Confirm after advance verified */}
                    {order.status === 'PENDING' &&
                     order.paymentOption === 'PARTIAL_COD' &&
                     order.advancePaymentOption === 'MANUAL_UPI' &&
                     (order.paymentStatus === 'PARTIALLY_PAID' || order.paymentStatus === 'PAID') && (
                      <button
                        onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Confirm
                      </button>
                    )}

                    {/* Mobile: PARTIAL_COD + PHONEPE/RAZORPAY — Awaiting Payment */}
                    {order.status === 'PENDING' &&
                     order.paymentOption === 'PARTIAL_COD' &&
                     (order.advancePaymentOption === 'PHONEPE' || order.advancePaymentOption === 'RAZORPAY') &&
                     order.paymentStatus === 'PENDING' && (
                      <span className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Awaiting Payment
                      </span>
                    )}

                    {/* Mobile: PARTIAL_COD + PHONEPE/RAZORPAY — Advance paid, confirm */}
                    {order.status === 'PENDING' &&
                     order.paymentOption === 'PARTIAL_COD' &&
                     (order.advancePaymentOption === 'PHONEPE' || order.advancePaymentOption === 'RAZORPAY') &&
                     (order.paymentStatus === 'PARTIALLY_PAID' || order.paymentStatus === 'PAID') && (
                      <button
                        onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Confirm
                      </button>
                    )}

                    {/* Mobile: Non-Partial-COD standard Confirm */}
                    {order.status === 'PENDING' &&
                     order.paymentOption !== 'PHONEPE' &&
                     order.paymentOption !== 'RAZORPAY' &&
                     order.paymentOption !== 'PARTIAL_COD' && (
                      <button
                        onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Confirm
                      </button>
                    )}

                    {order.status === 'CONFIRMED' && (
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowShippingModal(true);
                        }}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Package className="w-3 h-3 mr-1" />
                        Ship
                      </button>
                    )}

                    {order.status === 'SHIPPED' && (
                      <button
                        onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'DELIVERED'), 'Are you sure you want to mark this order as delivered?')}
                        className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Deliver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Mobile Pagination */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {renderPagination()}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {showShippingModal && selectedOrder && (
        <ShippingDetailsModal
          isOpen={showShippingModal}
          orderId={selectedOrder.orderId}
          isLoading={isShipping}
          onClose={() => setShowShippingModal(false)}
          onConfirm={async (shippingDetails) => {
            console.log(shippingDetails);
            try {
              await handleStatusUpdate(selectedOrder._id, 'SHIPPED', shippingDetails);
              // Only close modal after successful shipping
              setShowShippingModal(false);
            } catch (error) {
              // Modal stays open if there's an error
              console.error('Failed to ship order:', error);
            }
          }}
        />
      )}

      {showConfirmationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Action</h3>
              <p className="text-sm text-gray-500 mb-6">{confirmationMessage}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmationAction();
                    setShowConfirmationModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;